import { IMedia } from '@features/resort/types'
import { isVideoUrl } from './mediaUtils'

/**
 * Transforms legacy resort data format (primaryImage + images array) to new media array structure.
 * This utility converts the backend response to include a unified media array.
 *
 * @param resortData - Resort data from backend (may have primaryImage and images)
 * @returns Resort data with media array added (or existing media array preserved)
 */
export function transformResortMedia(resortData: any): any {
  if (!resortData) return resortData

  // If media array already exists, preserve it (future-proofing)
  if (resortData.media && Array.isArray(resortData.media) && resortData.media.length > 0) {
    return resortData
  }

  const mediaArray: IMedia[] = []
  const seenUrls = new Set<string>() // Track URLs to prevent duplicates

  // Add primary image/video first if it exists
  if (resortData.primaryImage) {
    seenUrls.add(resortData.primaryImage)
    mediaArray.push({
      type: isVideoUrl(resortData.primaryImage) ? 'video' : 'image',
      url: resortData.primaryImage,
      alt: resortData.primaryImageAltText || resortData.name || '',
      thumbnailUrl: undefined, // Can be added later when available
      caption: undefined,
    })
  }

  // Add images from images array (skip duplicates and primaryImage)
  let imagesArray: any[] = []
  if (Array.isArray(resortData.images)) {
    imagesArray = resortData.images
  } else if (resortData.images?.gallery) {
    // Handle nested gallery structure - could be gallery.amenities or gallery directly
    if (Array.isArray(resortData.images.gallery.amenities)) {
      imagesArray = resortData.images.gallery.amenities
    } else if (Array.isArray(resortData.images.gallery)) {
      imagesArray = resortData.images.gallery
    }
  }

  imagesArray.forEach((img) => {
    // Skip if no URL, already seen, or same as primary image
    if (!img.url || seenUrls.has(img.url)) {
      return
    }

    seenUrls.add(img.url)
    mediaArray.push({
      type: isVideoUrl(img.url) ? 'video' : 'image',
      url: img.url,
      alt: img.alt || resortData.name || '',
      thumbnailUrl: img.thumbnailUrl, // Use if provided
      caption: img.caption,
    })
  })

  // Add media array to resort data
  return {
    ...resortData,
    media: mediaArray.length > 0 ? mediaArray : undefined,
  }
}
